import {
  Component,
  Input,
  SimpleChanges,
  ChangeDetectorRef,
  OnInit,
  OnChanges,
  OnDestroy
} from '@angular/core';
import { AlertController } from '@ionic/angular';
import { addDays, lastDayOfMonth } from 'date-fns';
import { Avtaler } from 'src/app/services/avtale/avtale.types';
import { Anlegg } from 'src/app/services/forbruk/forbruk.types';
import { hasChanged } from 'src/app/utils/change-utils';
import { StromstotteService } from 'src/app/services/stromstotte/stromstotte.service';
import { Observable, Subscription } from 'rxjs';
import { SupportReceivedResult } from 'src/app/services/stromstotte/stromstotte.types';

@Component({
  selector: 'app-stromstotte-month',
  templateUrl: './month.component.html',
  styleUrls: ['./month.component.scss']
})
export class MonthComponent implements OnInit, OnChanges, OnDestroy {
  @Input() valgtAnlegg: Anlegg;
  @Input() valgtAvtale: Avtaler;
  @Input() foretakMap: Map<number, string>;
  @Input() selectedDate: Date;

  recievesStromstotte: boolean;
  estimatedStromstotte: number;
  currentDate: Date;
  isCurrentMonth: boolean;
  month: string;
  currentYear: string;
  currentDay: string;
  hasFetchingError: boolean;
  showStromstotte: boolean;
  showLoader: boolean;
  hasHistoricalCalculation: boolean;
  stromstotteResult$: Observable<SupportReceivedResult>;
  subscription: Subscription;

  constructor(
    private _changeDetectorRef: ChangeDetectorRef,
    private _alertController: AlertController,
    private _stromstotteService: StromstotteService
  ) {
    this.recievesStromstotte = false;
    this.showStromstotte = true;
    this.estimatedStromstotte = 0;
    this.currentDate = addDays(new Date(), -1);
    this.currentYear = this.currentDate.toLocaleDateString('nb-NO', {
      year: 'numeric'
    });
  }

  ngOnInit() {
    this.stromstotteResult$ = this._stromstotteService.stromstotteResult$;
    this.hasHistoricalCalculation = new Date(2023, 8, 1) > this.selectedDate;
    this.isCurrentMonth =
      this.currentDate.getMonth() === this.selectedDate.getMonth() &&
      this.currentDate.getFullYear() === this.selectedDate.getFullYear();
    this.getDateMonth();
    this.getStromstotte();
  }

  getDateMonth() {
    this.month = this.selectedDate.toLocaleDateString('nb-NO', {
      month: 'long'
    });
    if (this.isCurrentMonth) {
      this.currentDay = this.currentDate.toLocaleDateString('nb-NO', {
        day: 'numeric'
      });
    } else {
      this.currentDay = lastDayOfMonth(this.selectedDate)
        .toLocaleDateString('nb-NO', {
          day: 'numeric'
        });
    }
  }

  getStromstotte() {
    this.getDateMonth();

    if (!this.hasHistoricalCalculation) {
      this.subscription = this.stromstotteResult$.subscribe({
        next: (res) => {
          this.showLoader = true;
          if (res === undefined) {
            this.hasFetchingError = true;
          }
          this.hasFetchingError = false;
          const valgtAnleggStotte = res;
          this.estimatedStromstotte = valgtAnleggStotte?.receivesTotal || 0;
          this.recievesStromstotte = this.estimatedStromstotte > 0;
          this.showStromstotte = true;
          this.showLoader = false;
          this._changeDetectorRef.detectChanges();
        },
        error: () => {
          this.hasFetchingError = true;
        }
      });
    }
  }

  ngOnDestroy() {
    this.subscription?.unsubscribe();
  }

  ngOnChanges(changes: SimpleChanges) {
    if (
      !(
        changes?.valgtAvtale?.firstChange ||
        changes?.valgtAnlegg?.firstChange ||
        changes?.foretakMap?.firstChange ||
        changes?.selectedDate?.firstChange
      )
    ) {
      if (
        this.valgtAnlegg &&
        this.selectedDate &&
        this.valgtAvtale &&
        hasChanged([
          changes?.valgtAvtale,
          changes?.valgtAnlegg,
          changes?.foretakMap,
          changes?.selectedDate
        ])
      ) {
        this.hasHistoricalCalculation =
          new Date(2023, 8, 1) > this.selectedDate;
        this.isCurrentMonth =
          this.currentDate.getMonth() === this.selectedDate.getMonth() &&
          this.currentDate.getFullYear() === this.selectedDate.getFullYear();
        this.getStromstotte();
      }
    }
  }

  toCommaNumber(num: number) {
    return num.toFixed(2).replace('.', ',');
  }

  toRoundNumber(num: number) {
    return Math.round(num).toLocaleString('nb-NO');
  }

  async expandTooltip() {
    const initalert = await this._alertController.create({
      cssClass: 'push-alert',
      mode: 'ios',
      header: 'Foreløpig beregnet strømstøtte',
      message:
        '<p>Fra 1.september 2023 beregnes strømstøtten per time.' +
        ' Du får støtte for 90% av kostnaden når spotprisen for timen er over 73 øre, ekskludert moms.</p>' +
        '<p>Hittil i ' +
        this.month +
        ' anslår vi at du får en strømstøtte på ' +
        '<strong>' +
        this.toCommaNumber(this.estimatedStromstotte) +
        '</strong> kr.</p>' +
        '<p> Vi tar forbehold om feil, siden dette er en estimert beregning. Endelig strømstøtte vil fremkomme på din strømregning.</p>',
      buttons: ['Lukk']
    });
    await initalert.present();
  }

  async expandHistoricalStotte() {
    const message = `Grunnet endring av strømstøtten vil historikk før 1.september 2023 kun være
        tilgjengelig i fakturakopi under Regninger. Strømstøtten er ikke
        inkludert i kostnader i forbruksvisningen før denne datoen.
    `;
    const initalert = await this._alertController.create({
      cssClass: 'push-alert',
      mode: 'ios',
      header: 'Historisk strømstøtte',
      message,
      buttons: ['Lukk']
    });
    await initalert.present();
  }
}
